import React from "react";

function ListBlock({ title, items, emptyText = "No items available." }) {
  return (
    <div className="analysis-block">
      <h4>{title}</h4>
      {items && items.length > 0 ? (
        <ul className="analysis-list">
          {items.map((item, index) => (
            <li key={`${title}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">{emptyText}</p>
      )}
    </div>
  );
}

function getStageText(stage) {
  const map = {
    queued: "Queued for analysis...",
    reading_questionnaire: "Reading questionnaire...",
    checking_symptoms: "Extracting symptoms and history...",
    checking_red_flags: "Checking red flags...",
    streaming_medgen_notes: "Preparing live Medgen notes...",
    structuring_report: "Structuring final report...",
    loading_questionnaire: "Reading questionnaire...",
    analysing_symptoms: "Analysing pre-diagnosis...",
    preparing_clinical_notes: "Preparing clinical notes...",
    completed: "Analysis completed.",
    failed: "Analysis failed.",
  };

  return map[stage] || "Analysing pre-diagnosis...";
}

function AnalysisLoading({ stage, progress, streamedText }) {
  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h2>Medgen Analysis</h2>
          <p className="muted">
            Please wait while your report is being prepared.
          </p>
        </div>
      </div>

      <div className="analysis-loading">
        <div className="loader" />

        <h3>{getStageText(stage)}</h3>
        <p className="muted">Progress: {progress || 10}%</p>

        <div className="ai-image-progress">
          <div style={{ width: `${progress || 10}%` }} />
        </div>

        {streamedText ? (
          <div className="analysis-stream-box">
            <h4>Live Medgen notes</h4>
            <pre>{streamedText}</pre>
          </div>
        ) : (
          <p className="muted">
            Reading details and preparing the first clinical draft...
          </p>
        )}
      </div>
    </div>
  );
}

export default function AnalysisPanel({
  analysis,
  sessionStatus,
  streaming = false,
  streamStage = "",
  streamProgress = 0,
  streamedText = "",
}) {
  const isEmptyAnalysis =
    analysis &&
    !analysis.initial_summary &&
    !analysis.possible_concerns?.length &&
    !analysis.suggested_tests?.length &&
    !analysis.suggested_specialists?.length &&
    !analysis.medicine_concerns?.length &&
    !analysis.brain_regions?.length &&
    !analysis.doctor_note;

  const isProcessing =
    streaming ||
    sessionStatus === "analysis_pending" ||
    sessionStatus === "analysis_processing" ||
    sessionStatus === "analysis_streaming" ||
    analysis?.status === "pending" ||
    analysis?.status === "processing" ||
    analysis?.status === "queued" ||
    isEmptyAnalysis;

  if (isProcessing) {
    return (
      <AnalysisLoading
        stage={streamStage || analysis?.stage}
        progress={streamProgress || analysis?.progress}
        streamedText={streamedText}
      />
    );
  }

  if (!analysis) {
    return (
      <div className="card">
        <div className="section-head">
          <div>
            <h2>Medgen Analysis</h2>
            <p className="muted">
              Save the questionnaire to generate a structured medical summary.
            </p>
          </div>
        </div>

        <div className="empty-state-box">
          <h3>No analysis yet</h3>
          <p className="muted">
            Once the questionnaire is saved, Medgen will prepare the clinical summary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h2>Medgen Analysis</h2>
          <p className="muted">
            Structured output generated from the current questionnaire and session context.
          </p>
        </div>
      </div>

      <div className="analysis-summary-card">
        <h3>Initial Summary</h3>
        <p>{analysis.initial_summary || "No summary available."}</p>
      </div>

      <div className="analysis-grid">
        <ListBlock title="Possible Concerns" items={analysis.possible_concerns} />
        <ListBlock title="Suggested Tests" items={analysis.suggested_tests} />
        <ListBlock title="Suggested Specialists" items={analysis.suggested_specialists} />
        <ListBlock title="Medicine Concerns" items={analysis.medicine_concerns} />
        <ListBlock title="Brain Regions" items={analysis.brain_regions} />
      </div>

      <div className="analysis-doctor-note">
        <h3>Doctor Note</h3>
        <p>{analysis.doctor_note || "No doctor note available."}</p>
      </div>
    </div>
  );
}